import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Dialog, DialogContent, List, Typography } from '@mui/material';

import OptionItem from '../onboarding/OnboardingOptionItem';
import DialogActions from '../common/DialogActions';
import useScreenSize from '../../utils/useScreenSize';
import CancelIcon from '../../assets/subscription/cancel.svg';
import { cancellationReasons } from '../../constants';
import { palette, spaces } from '../../assets/styles/themes/tokens';

interface StripeCancelDialogProps {
  open: boolean;
  isTrial: boolean;
  isLoading: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}

const StripeCancelDialog: React.FC<StripeCancelDialogProps> = (props) => {
  const { open, isTrial, isLoading, onClose, onConfirm } = props;
  const { t } = useTranslation();
  const { isXsScreen, isSmScreen } = useScreenSize();
  const [option, setOption] = useState<string>('');

  // Reset the selection so a previous choice doesn't linger on reopen.
  useEffect(() => {
    if (open) {
      setOption('');
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={isLoading ? undefined : onClose}
      fullWidth
      maxWidth='lg'
      aria-labelledby='stripe-cancel-dialog-title'
    >
      <DialogContent sx={{ p: 0 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: isXsScreen || isSmScreen ? 'column' : 'row',
            alignItems: 'flex-start',
            flex: '1 0 0',
            alignSelf: 'stretch',
          }}
        >
          {!isXsScreen && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                alignSelf: isSmScreen ? 'center' : 'flex-start',
                '& svg': {
                  width: '400px',
                  height: '300px',
                },
              }}
            >
              <CancelIcon />
            </Box>
          )}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              padding: spaces.lg,
              justifyContent: 'center',
              alignItems: 'flex-start',
              gap: spaces.lg,
              flex: '1 0 0',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: spaces.xs }}>
              <Typography id='stripe-cancel-dialog-title' variant='h6' sx={{ color: palette.text.primary }}>
                {t('Too bad you want to leave!')}
              </Typography>
              <Typography variant='body2' sx={{ color: palette.text.secondary }}>
                {t(
                  isTrial
                    ? 'You can enjoy your premium benefits until the end of your trial'
                    : 'Once cancelled, your subscription will not renew. ' +
                      'You keep full access until the end of your current billing period.'
                )}
              </Typography>
              <Typography variant='body2' sx={{ color: palette.text.secondary }}>
                {t('Your feedback helps us make myLessons even better. What is the main reason for your resignation?')}
              </Typography>
            </Box>
            <List
              dense
              sx={{
                padding: 0,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: spaces.md,
              }}
            >
              {cancellationReasons.map((reason) => (
                <OptionItem
                  key={reason}
                  label={reason}
                  isSelected={reason === option}
                  isRadioButton={true}
                  onClick={() => setOption(reason)}
                  sxProps={{ padding: spaces.md }}
                />
              ))}
            </List>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions
        primaryButton={{
          text: t(isTrial ? 'Cancel Trial Subscription' : 'Cancel Subscription'),
          color: 'error',
          variant: 'contained',
          onClick: () => onConfirm(option),
          loading: isLoading,
          disabled: !option,
        }}
        secondaryButton={{
          text: t('Keep subscription'),
          onClick: onClose,
          disabled: isLoading,
        }}
      />
    </Dialog>
  );
};

export default StripeCancelDialog;
